import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { Bin } from '../../shared/icons';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { Text } from '../../shared/text/Text';
import { deleteOpinionSubCounterpartyTypesStarted, getIsDeletingSubCounterparties, getLinkedSubCounterpartyTypes, getSavedParentCounterpartyTypes, getSavedSelectedSubCounterpartyType, getSubCounterpartiesToDelete, updateSubCounterpartiesToDelete } from './store';
import styles from './SubCounterpartyTypes.module.scss';
import { Toggle } from '../../shared/toggle';
import { Scrollable } from '../../shared/scrollable/Scrollable';

const { lightestGrey, white } = styles;

export const DeleteConfirmationModal: React.FC = () => {
    const [deletePledge, setDeletePledge] = useState<string>('');
    const dispatch = useAppDispatch();

    const savedSelectedSubCounterparty = useAppSelector(getSavedSelectedSubCounterpartyType);
    const subCounterpartiesToDelete = useAppSelector(getSubCounterpartiesToDelete);
    const isDeleting = useAppSelector(getIsDeletingSubCounterparties);
    const savedParentCounterpartyTypes = useAppSelector(getSavedParentCounterpartyTypes);
    const linkedSubCounterpartyTypes = useAppSelector(getLinkedSubCounterpartyTypes);

    const closeModal = useCallback(() => dispatch(updateSubCounterpartiesToDelete(null)), [dispatch]);
    const deleteSubCounterpartyType = useCallback(() => dispatch(deleteOpinionSubCounterpartyTypesStarted()), [dispatch]);

    const deleteButtonDisabled = deletePledge !== 'delete';
    const deleteButtonDisabledTooltip = deleteButtonDisabled ? ['Please type delete above to confirm delete'] : [];

    const name = useMemo(() => savedSelectedSubCounterparty?.subCounterpartyName || '', [savedSelectedSubCounterparty]);
    const deletingMultiple = useMemo(() => !isNull(subCounterpartiesToDelete) && subCounterpartiesToDelete.length > 1, [subCounterpartiesToDelete]);

    const warningLabel = useMemo(() => `Once deleted, ${deletingMultiple ? 'any of ' : ''}the Sub Counterpart${deletingMultiple ? 'ies' : 'y'} that ${deletingMultiple ? 'are' : 'is'} referenced within any Opinions will be removed. This cannot be undone.`, [deletingMultiple]);

    const onDelete = useCallback(() => {
        deleteSubCounterpartyType();
        setDeletePledge('');
    }, [setDeletePledge, deleteSubCounterpartyType]);

    const onKeydown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === 'enter' && !deleteButtonDisabled) {
            onDelete();
        }
    }, [deleteButtonDisabled, onDelete]);

    const onClose = useCallback(() => {
        closeModal();
        setDeletePledge('');
    }, [closeModal, setDeletePledge]);

    const getIsChecked = useCallback((parentCounterparty: string) => !isNull(subCounterpartiesToDelete) && subCounterpartiesToDelete.map(({ parentCounterpartyType }) => parentCounterpartyType).includes(parentCounterparty), [subCounterpartiesToDelete]);

    const toggleParentCounterparty = useCallback((isChecked: boolean, parentCounterparty: string) => {
        const foundLinkedSubCounterparty = linkedSubCounterpartyTypes.find(({ parentCounterpartyType }) => parentCounterpartyType === parentCounterparty);
        if (foundLinkedSubCounterparty && !isNull(subCounterpartiesToDelete)) {
            const updatedSubCounterpartiesToDelete = isChecked ? [...subCounterpartiesToDelete, foundLinkedSubCounterparty] : subCounterpartiesToDelete.filter(({ parentCounterpartyType }) => parentCounterpartyType !== parentCounterparty);
            dispatch(updateSubCounterpartiesToDelete(updatedSubCounterpartiesToDelete));
        }
    }, [linkedSubCounterpartyTypes, subCounterpartiesToDelete, dispatch]);

    const getIsDisabled = useCallback((parentCounterparty: string) => savedSelectedSubCounterparty?.parentCounterpartyType === parentCounterparty, [savedSelectedSubCounterparty]);

    const multipleParentContent = useMemo(() => {
        if (savedParentCounterpartyTypes.length === 1) {
            return null;
        }
        return (
            <div className={styles.multiParentWrapper}>
                <div className={styles.multiParentLabel}>Would you like to also delete this Sub Counterparty Type for any of the other linked parent counterparties?</div>
                <div className={styles.multiParentOptions}>
                    <Scrollable maxHeight='200px'>
                        {savedParentCounterpartyTypes.map((parentCounterparty, index) => {
                            const checked = getIsChecked(parentCounterparty);
                            const disabled = getIsDisabled(parentCounterparty);
                            return (
                                <div key={parentCounterparty} className={styles.deleteParentCounterpartyWrapper} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                    <div className={styles.deleteParentCounterpartyLabel}>{parentCounterparty}</div>
                                    <div className={styles.deleteParentCounterpartyToggle}>
                                        <Toggle
                                            checked={checked}
                                            onChange={isChecked => toggleParentCounterparty(isChecked, parentCounterparty)}
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>
            </div>
        );
    }, [savedParentCounterpartyTypes, getIsChecked, toggleParentCounterparty, getIsDisabled]);

    if (isNull(subCounterpartiesToDelete)) {
        return null;
    }

    return (
        <ConfirmationModal
            isOpen
            closeModal={onClose}
            confirmLabel='Delete'
            testId='delete-sub-counterparties'
            confirm={onDelete}
            confirmDisabled={deleteButtonDisabled}
            confirmDisabledTooltip={deleteButtonDisabledTooltip}
            zIndex={15}
        >
            <ModalHeader label='Delete Sub Counterparty' icon={Bin} testId='delete-sub-counterparties-modal' />
            <div className={styles.deleteConfirmationWrapper} data-testid='delete-sub-counterparties-modal-wrapper'>
                {isDeleting ? (<PageLoadingDots label={`Deleting Sub Counterpart${deletingMultiple ? 'ies' : 'y'}...`} />) : (
                    <div>
                        <div className={styles.warningTitle} data-testid='delete-sub-counterparties-warning-title'>Are you sure you want to delete this Sub Counterparty?</div>
                        <div className={styles.name} data-testid='delete-sub-counterparties-name'>{name}</div>
                        <div className={styles.warningLabel} data-testid='delete-sub-counterparties-warning-label'>{warningLabel}</div>
                        {multipleParentContent}
                        <div className={styles.confirmationInputLabel} data-testid='delete-sub-counterparties-instruction'>Type delete in the box below to confirm deletion of Sub Counterpart{deletingMultiple ? 'ies' : 'y'}.</div>
                        <Text
                            value={deletePledge}
                            placeholder='delete'
                            onChange={e => setDeletePledge(e.target.value)}
                            onKeyDown={onKeydown}
                            testId='delete-sub-counterparties-pledge'
                            autoFocus
                        />
                    </div>
                )}
            </div>
        </ConfirmationModal>
    );
};
